const caseStudiesRevenueGrowth = [
    {
      slug: "scaling-cvm-with-aws-mlops",
      title: "Scaling Customer Value Management with AWS-Powered MLOps",
      businessProblem:
        "A major telco was facing inefficiencies in its customer engagement campaigns. CVM initiatives were manually executed and intuition-driven, resulting in poor targeting, low campaign conversion (7% in Jan 2023), and high operational effort. Additionally, reliance on on-premise infrastructure created roadblocks to model updates, experimentation, and scalability.",
      approach:
        "OBZ implemented a cloud-native CVM solution using AWS MLOps services to automate and optimize campaign delivery. The new setup enabled real-time decisioning, continuous model updates, and scalable campaign orchestration—replacing manual processes with automated, insight-driven targeting.",
      solutionHighlights: [
        "Deployed Amazon SageMaker for training, versioning, and deploying predictive models",
        "Used AWS Lambda & API Gateway to run inference, apply business logic, and support A/B testing",
        "Integrated with Amazon S3 for storing historical model outputs and customer features",
        "Automated workflows using AWS Glue & CloudWatch for monitoring, ETL, and logging",
        "Enabled real-time personalization across channels using data-driven triggers",
      ],
      impact: [
        "Campaign efficiency increased from 7% to 55% over 12 months",
        "Scaled campaign reach from 1 million to 30 million subscribers",
        "Drove 1.7x higher revenue per user and 2x ARPU uplift through targeted, ML-powered offers",
      ],
      image: "/images/revenue-growth/OBZ Case Study Images Outcomes Revenue.png"
    },
    {
      slug: "agentic-voice-ai-broadband-upsell",
      title: "Agentic Voice AI for Scalable Broadband Upsell",
      businessProblem:
        "The client wanted to increase plan upgrades among existing broadband users, but traditional telemarketing was costly, inconsistent, and difficult to scale especially across languages and user personas.",
      approach:
        "OBZ designed a multilingual, GenAI-powered outbound voice agent, capable of conducting full upsell conversations. Built using OBZ’s GenAI Studio, the solution was customized to: Speak in both English and Tagalog, Deliver personalized recommendations, Adjust tone in real time using sentiment detection.",
      solutionHighlights: [
        "AI Voice Agent with Sentiment Awareness: Adaptive personas that shift based on detected user mood.",
        "Real-Time Personalization: Offers and language tailored per call, based on data and intent signals.",
        "Multilingual Capability: Natively fluent in both English and Tagalog, enhancing engagement.",
        "Fully Automated Workflow: From call dialing to pitch delivery and conversion tracking.",
      ],
      impact: [
        "50% Reduction in Cost Per Conversion compared to human agents",
        "12% Increase in average daily conversions",
        "Consistent customer experience across thousands of calls per week",
        "Scalable model replicable for upsell, renewals, and cross-sell use cases",
      ],
      image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Revenue2.png",
    },
    {
      slug: "ml-driven-nbo-for-cvm",
      title: "Scaling Customer Value Management with ML-Driven NBO",
      businessProblem:
        "A leading telecom provider relied on manual, rule-based campaigns with minimal experimentation and limited personalization resulting in stagnant growth and poor conversion impact. The CVM team faced performance, latency, and scale constraints on their on-prem infrastructure and needed a smarter, elastic system for real-time targeting and revenue uplift.",
      approach:
        "OBZ partnered with the client to build a comprehensive ML-powered CVM framework hosted partially on AWS to automate Next-Best-Offer (NBO) generation, A/B testing, and model optimization at scale.",
      solutionHighlights: [
        "Ensemble NBO Models: Combined customer propensity and business value to predict offer impact on ARPU.",
        "Persuadability Modeling: Scored likelihood of uptake when bonus offers were present.",
        "Real-Time Inference via AWS: Deployed optimized XGBoost & TabNet models on Sagemaker with Lambda, API Gateway, and DynamoDB.",
        "Automated A/B Testing Framework: Continuously evaluated 60+ model configurations across population segments.",
        "Elastic Scaling: Leveraged cloud infra to overcome on-prem limits and handle bursty traffic.",
      ],
      keyInnovations: [
        "Campaign-specific spiels auto-generated and optimized for tone/message variants.",
        "Full MLOps pipeline on AWS reduced model deployment time significantly.",
        "Cloud-native orchestration enabled seamless integration with existing on-prem systems.",
      ],
      impact: [
        "2X improvement in campaign efficiency vs. baseline rule-based campaigns",
        "2X increase in incremental ARPU over 12 months",
        "1.7X increase in per subscriber revenue",
        "Subscriber base for ML campaigns grew from 1M to 6M",
        "Helped CVM team exceed annual revenue targets",
        "Enabled real-time inbound and outbound campaign use-cases",
        "Reduced time-to-market for new ML models and use cases",
      ],
      image:
      "/images/revenue-growth/OBZ Case Study Images Outcomes Revenue3.png",
    },
  ];
  
  export default caseStudiesRevenueGrowth;
  

  export const caseStudiesOperational = [
    {
      slug: "data-migration-with-cloud-platform",
      title: "Project Alapaap: Modernizing Data Platforms for Scalability & Efficiency",
      businessProblem: [
        "High complexity from legacy systems and fragmented data pipelines",
        "Data quality issues due to inconsistent governance and lack of standardization",
        "Rising costs of infrastructure maintenance",
        "Performance bottlenecks for AI/ML workloads",
        "Limited agility and slower decision-making due to inefficient workflows",
      ],
      approach:
        "OBZ led a comprehensive cloud modernization initiative, focused on building a unified, scalable platform. This involved AI-driven migration planning, standardization of governance and lineage, and platform architecture optimized for real-time analytics.",
      solutionHighlights: [
        "Cloud-Native Data Modernization: Migrated 13+ PB of data from Cloudera, Oracle, and Teradata to AWS & Databricks",
        "Optimized ETL Pipelines: Transitioned 1500+ jobs using AI-enabled frameworks",
        "Data Governance & Standardization: Automated cataloguing, lineage tracking, and compliance workflows",
        "High-Performance Architecture: Built a data mesh ensuring single source of truth",
        "Automation & Cost Optimization: Deployed orchestration tools and AI-powered workload schedulers",
      ],
      impact: [
        "40–50% Faster Data Processing Conversions",
        "Increased ROI via reduced infrastructure overhead and faster execution",
        "Enhanced Scalability for real-time, AI-enabled decisioning",
        "Improved Data Security and compliance across data operations",
      ],
      image:
        "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost.png",
    },
    {
      slug: "retail-shelf-analytics-with-ai",
      title: "Optimizing In-Store Product Placement with Computer Vision and ML",
      businessProblem: [
        "Declining sales due to poor in-store product visibility",
        "No shelf analytics tools or real-time insights",
        "Manual shelf audits were time-consuming and unscalable",
      ],
      approach:
        "Implemented an AI-based image analytics model using CCTV footage to generate product visibility insights. Leveraged Amazon SageMaker for ML model training, and Rekognition for object detection. Dashboards were built using Amazon QuickSight for actionable store-level analytics.",
      solutionHighlights: [
        "CCTV video frames processed and labelled using Amazon SageMaker Ground Truth",
        "Product visibility models trained with Amazon Rekognition",
        "Integrated pipelines with DynamoDB and Amazon QuickSight for real-time dashboards",
        "Enabled analytics on shelf gaps, stock-out risks, and underutilized zones",
        "Continuous model updates for improved detection accuracy",
      ],
      impact: [
        "+5% Increase in Sales Revenue",
        "-75% Reduction in Manual Audit Effort",
        "+20% Improvement in Product Allocation",
        "Real-Time Visibility ensured products stayed in customers’ line of sight",
        "Automated Stock-Out Alerts triggered every 2 hours",
      ],
      image:
      "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost2.png",
    },
    {
      slug: "network-planning-ml-capex-churn",
      title: "ML-Driven Network Planning to Optimize Capex & Churn",
      businessProblem: [
        "Stagnating user growth and rising churn in urban areas",
        "Network investment based on static reports, not real-time insights",
        "Inefficient capital deployment and missed retention opportunities",
      ],
      approach:
        "Developed a digital twin of the client’s network using anonymized mobility data and churn trends. Machine learning models were used to simulate network performance and predict churn likelihood at each site for targeted investment planning.",
      solutionHighlights: [
        "ML-based churn risk scoring across 1,000+ network sites",
        "Geo-mapping churn hotspots and correlating with network quality",
        "Simulated impact of network upgrades on retention",
        "Dashboard-driven investment recommendations",
        "Integrated with planning teams for rapid execution",
      ],
      impact: [
        "50% churn reduction opportunity across key urban zones",
        "THB 360 million projected revenue uplift",
        "Improved network QoS in churn-prone areas",
        "Faster, more data-driven tower investment decisions",
      ],
      conclusion:
        "By combining structured automation, AI agent specialization, and human validation, OBZ delivered a modern, scalable, and cost-effective migration strategy for ETL workloads. The ETL Crew is now a repeatable framework to modernize pipelines, reduce risk, and unlock long-term efficiency.",
        image:
        "/images/revenue-growth/OBZ Case Study Images OutcomesOperational Cost3.png",
    },
  ];
  
  export const caseStudiesCustomerExperience = [
    {
      slug: "custom-chat-agent-at-scale",
      title: "Custom Chat Agent at Scale",
      industry: "Telecom",
      region: "Not specified",
      businessProblem: [
        "Legacy chatbot systems were inflexible, developer-dependent, and not scalable for high-traffic channels like WhatsApp and web chat."
      ],
      approach:
        "Deployed GenAI Studio, a no-code platform for business teams to build and update chatbot flows independently.",
      solutionHighlights: [
        "50+ customer-facing chat flows",
        "Drag-and-drop no-code interface",
        "Enterprise knowledge integration",
        "Scalable and secure architecture"
      ],
      impact: [
        "50 automated chat flows launched across app, web, and messaging platforms",
        "30–40% reduction in development time for future chatbot use cases",
        "Significantly lowered IT dependency; marketing and CX teams manage chatbot updates",
        "Established a repeatable framework for scalable GenAI-led CX transformation"
      ],
      image: "/images/revenue-growth/OBZ Case Study Images Outcomes Customer Engagement.png"
    },
    {
      slug: "reactivating-dormant-users-cvm",
      title: "Reactivating Dormant Users with ML-Powered CVM",
      industry: "Digital Banking",
      region: "Southeast Asia",
      businessProblem: [
        "High user inactivity despite new app features",
        "Manual promotions were ineffective for re-engagement"
      ],
      approach:
        "Built ML models for churn analysis and used modular CVM templates for targeted reactivation campaigns.",
      solutionHighlights: [
        "Churn prediction and behavioral segmentation using ML",
        "Modular CVM templates for journey orchestration",
        "Personalized, experiment-backed nudges and offers",
        "Real-time tracking dashboards with A/B testing"
      ],
      impact: [
        "2.5× increase in conversion for dormant user campaigns",
        "Projected 1.5 Billion VND in incremental revenue uplift",
        "Improved app session frequency and duration among reactivated users",
        "CVM playbooks created for sustained reactivation efforts by internal teams"
      ],
      image: "/images/revenue-growth/OBZ Case Study Images Outcomes Customer Engagement2.png"
    },
    {
      slug: "ai-responder-for-social-media",
      title: "80% Less Manual Response Load with AI-Powered Social Media Responder",
      industry: "Telecom",
      region: "ASEAN",
      businessProblem: [
        "Overwhelmed agents due to manual social media responses",
        "Poor engagement and missed leads"
      ],
      approach:
        "Deployed Auto-Wall Responder with LLMs and CRM integration for real-time, brand-aligned replies.",
      solutionHighlights: [
        "Intent-based auto-replies for FAQs and transactional queries",
        "Native support for TikTok, Facebook, X, and LinkedIn",
        "Persona-aligned GenAI responses matching brand tone",
        "Real-time Salesforce CRM integration for human handover"
      ],
      impact: [
        "80% reduction in manual agent response load",
        "2.5× increase in engagement across all supported platforms",
        "USD 228,571 projected cost savings by 2025",
        "Improved brand consistency and CX quality on social channels",
        "Reduced lead leakage and faster response to high-intent queries"
      ],
      image: "/images/revenue-growth/OBZ Case Study Images Outcomes Customer Engagement3.png"
    }
  ];
  
  export const caseStudiesDetailedCX = [
    {
        image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise.png",
      slug: "scaling-agents-with-genai-studio",
      title: "Scaling Agent Deployment with GenAI Studio",

      businessProblem: `A leading telco aimed to accelerate its rollout of GenAI use cases—especially conversational agents—but faced internal bottlenecks. Existing approaches required engineering effort for each use case, lacked governance standards, and didn’t offer business users the tools to experiment or deploy independently.`,
      approach: `OBZ deployed its GenAI Studio within the client’s AWS environment to serve as a centralized, secure platform for agent lifecycle management. The studio offered business-friendly interfaces to build, test, deploy, and manage AI coworkers across customer-facing and internal workflows.`,
      solutionHighlights: [
        "Low-Code Agent Builder: Create AI coworkers using prompts, memory, tool access (MCPs), and goal-setting",
        "Omnichannel Enablement: Voice and chat flows launched from a single platform",
        "Command Center: Centrally manage roles, workflows, approvals, and metrics",
        "Enterprise Guardrails: Defined agent boundaries for safety, compliance, and LLM evaluations",
        "Embedded Knowledgebases: Inject organizational context for intelligent, relevant responses",
        "Scalable Architecture: Fully deployed on AWS Bedrock, aligned with AgentCore",
      ],
      impact: [
        "50+ AI flows deployed across multiple departments",
        "30 to 40% reduction in time-to-launch for future use cases",
        "Shift from one-off pilots to scalable, governed agentic infrastructure",
        "Empowered non-technical teams to independently manage AI coworkers",
      ],
    },
    {
        image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise2.png",
      slug: "etl-migration-agentic-ai",
      title: "Accelerating Legacy ETL Migration with Agentic AI",

      businessProblem: `A leading financial services enterprise was modernizing its data stack by moving from legacy ETL tools to a cloud-native pipeline ecosystem. The migration process was manual, time-consuming, and prone to logic drift—requiring extensive human effort and leading to slow timelines and increased costs.`,
      approach: `OBZ implemented a team of GenAI-powered agents capable of analyzing source jobs, rewriting code using natural language prompts, and validating outputs. Each agent was assigned a specific role (developer, quality checker, logic interpreter) and collaborated to deliver faster, more accurate migrations.`,
      solutionHighlights: [
        "NLP-driven code generation and rewriting based on existing job definitions",
        "Source-to-target logic mapping using embedded data context",
        "Memory-enabled agents for continuity across multi-step migrations",
        "Automated QA checks for validation, error detection, and rollback",
        "Seamless integration with AWS Glue and Spark for target environment deployment",
      ],
      impact: [
        "50% reduction in overall migration timelines",
        "60% of ETL pipelines migrated with no human intervention",
        "Significantly reduced engineering effort and rework",
        "More reliable, auditable, and scalable migration approach",
      ],
    },
    {
        image:
        "/images/revenue-growth/OBZ Case Study Images Outcomes Agentic Enterprise3.png",
      slug: "tara-hr-talent-agent",
      title: "TARA: OBZ’s Internal HR Agent for Streamlining Talent Shortlisting",
      businessProblem: `With a lean HR operations team and a growing volume of resumes across geographies, OBZ needed a faster, smarter way to screen candidates. Manual CV evaluation consumed significant HR bandwidth and delayed shortlisting and interview planning.`,
      approach: `We built an internal AI coworker named TARA (Talent Acquisition & Recruitment Agent) to automate resume screening and generate interview questions. TARA integrates with Zoho Recruit, Microsoft Teams, and internal email systems to support real-time collaboration across platforms.`,
      solutionHighlights: [
        "Automated CV Screening: Reviews resumes, checks for role-based skills, and scores candidates",
        "Interview Question Generation: Suggests questions based on JD and resume context",
        "Multi-platform Integration: Operates across Zoho Recruit, MS Teams, email, and phone workflows",
        "Human-in-the-loop Oversight: HR managers retain final selection and approval authority",
      ],
      impact: [
        "Substantial reduction in manual resume reviews",
        "Improved shortlisting consistency across geographies",
        "Freed up HR bandwidth for high-value interactions",
        "Created a scalable blueprint for agentic operations across internal teams",
      ],
    },
  ];
  